/**
 * BottomNavigation - Mobile-only bottom tab bar.
 *
 * Visible only on screens < lg (1024px).
 * Fixed to bottom, 4 navigation items.
 *
 * @accessibility WCAG 2.1 AA, BFSG min touch target 44px
 */

import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutDashboard, Grid3X3, Package, MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import { BottomNavMore } from './BottomNavMore';

const NAV_ITEMS = [
  { path: '/v2/dashboard', icon: LayoutDashboard, labelKey: 'mobileNav.dashboard' },
  { path: '/v2/tools', icon: Grid3X3, labelKey: 'mobileNav.tools' },
  { path: '/v2/products', icon: Package, labelKey: 'mobileNav.products' },
] as const;

export function BottomNavigation() {
  const { t } = useTranslation();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  const isActive = (path: string) => location.pathname.startsWith(path);

  return (
    <>
      <nav
        className="lg:hidden fixed bottom-0 left-0 right-0 z-50 h-16 bg-white dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-800 shadow-[0_-2px_10px_rgba(0,0,0,0.05)]"
        aria-label={t('mobileNav.menu')}
      >
        <div className="flex items-center justify-around h-full px-2">
          {NAV_ITEMS.map(({ path, icon: Icon, labelKey }) => {
            const active = isActive(path);
            return (
              <Link
                key={path}
                to={path}
                className={cn(
                  'flex flex-col items-center justify-center gap-1 min-w-[64px] min-h-[44px] rounded-lg px-3 py-1 transition-colors',
                  active
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200'
                )}
                aria-current={active ? 'page' : undefined}
              >
                <Icon className="h-5 w-5" />
                <span className="text-2xs font-medium leading-none">{t(labelKey)}</span>
              </Link>
            );
          })}

          {/* More Button */}
          <button
            onClick={() => setMoreOpen(!moreOpen)}
            className={cn(
              'flex flex-col items-center justify-center gap-1 min-w-[64px] min-h-[44px] rounded-lg px-3 py-1 transition-colors',
              moreOpen
                ? 'text-primary-600 dark:text-primary-400'
                : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200'
            )}
            aria-expanded={moreOpen}
            aria-haspopup="true"
          >
            <MoreHorizontal className="h-5 w-5" />
            <span className="text-2xs font-medium leading-none">{t('mobileNav.more')}</span>
          </button>
        </div>
      </nav>

      <BottomNavMore open={moreOpen} onClose={() => setMoreOpen(false)} />
    </>
  );
}
